import { useCallback } from 'react';
import dayjs from 'dayjs';
import { notification } from 'antd';
import { createApiGateway } from '../../../../apiGateway';
import { type FinancesFilters, type V2StatisticsParams } from '../../../../types/finances';

const apiGateway = createApiGateway();

const buildFilename = (filters: FinancesFilters): string => {
  const from = filters.dateRange[0];
  const to = filters.dateRange[1];
  const format = filters.format;
  const groupBy = filters.groupBy && filters.groupBy.length > 0 ? filters.groupBy.join(',') : 'none';
  // Use safe filename characters only
  const name = `finances_${from}_to_${to}_${format}_${groupBy}`.replace(/[^a-zA-Z0-9_\-.,]/g, '_');
  return `${name}.csv`;
};

type ExportCsvArgs = {
  filters: FinancesFilters;
  order: {
    by?: string;
    direction?: 'ASC' | 'DESC';
  };
  pagination: {
    current: number;
    pageSize: number;
  };
  shouldPaginate: boolean;
};

export const useExportFinancesCsv = () => {
  const exportCsv = useCallback(async ({ filters, order, pagination, shouldPaginate }: ExportCsvArgs) => {
    const params: V2StatisticsParams = {
      from: filters.dateRange[0],
      to: dayjs(filters.dateRange[1]).add(1, 'day').format('YYYY-MM-DD'),
    };

    if (filters.format !== 'summary') {
      params.format = filters.format;
    }

    if (filters.groupBy && filters.groupBy.length > 0) {
      params.groupBy = filters.groupBy.join(',');
    }

    if (filters.sspId && filters.sspId.length > 0) {
      params.sspId = filters.sspId.join(',');
    }

    if (filters.dspId && filters.dspId.length > 0) {
      params.dspId = filters.dspId.join(',');
    }

    if (filters.companyId && filters.companyId.length > 0) {
      params.companyId = filters.companyId.join(',');
    }

    if (filters.extraCols && filters.extraCols.length > 0) {
      params.extraCols = filters.extraCols.join(',');
    }

    if (order.by) {
      params.orderBy = order.by;
    }

    if (order.direction) {
      params.orderDirection = order.direction;
    }

    if (shouldPaginate) {
      params.limit = pagination.pageSize;
      params.offset = (pagination.current - 1) * pagination.pageSize;
    }

    try {
      const { blob, filename } = await apiGateway.statisticsV2.exportV2Statistics(params);

      const resolvedFilename = filename || buildFilename(filters);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', resolvedFilename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Attempt graceful error message for user
      const message = error instanceof Error ? error.message : 'Failed to export CSV. Please try again later.';
      notification.error({
        title: 'Export Failed',
        description: message,
      });
    }
  }, []);

  return { exportCsv };
};
